/**
 * Persists a logged-in customer's cart to customer.metafields.nfckey.live_cart
 * so it can be restored on another device (App Proxy: /apps/studio/api/embed/cart/sync).
 *
 * On load, first restores/merges any persisted cart (read from the
 * #nfckey-persisted-cart application/json script emitted sitewide from
 * layout/theme.liquid via snippets/nfckey-cart-persistence.liquid, #198 —
 * never from a raw JS assignment; see issue #192) into the current device's
 * cart, then enables sync. Sync fires on page load (catches the approved-proof
 * add, which redirects to /cart without a /cart/change or /cart/update event)
 * and on cart mutations, observed via the shared "nfckey:cart-mutated" event
 * dispatched by designer-cart-validation.js's existing fetch wrapper. Sync stays
 * disabled until restore resolves, so a fresh/empty device can never clobber a
 * good remote cart.
 *
 * #198: to reflect changes made on another device without a manual refresh,
 * GET /apps/studio/api/embed/cart/sync is polled every 20s (and on tab focus)
 * and reconciled. Reconcile always additively restores missing lines; it only
 * REMOVES a local line that it previously observed in the remote snapshot and
 * that has since disappeared from a newer one — never a line merely absent from
 * remote (that wrongly wiped freshly-added, not-yet-synced items). The initial
 * page-load reconcile is additive-only for the same reason.
 *
 * Also runs the cart-removal cascade (#191, independent of login state): diffs
 * /cart.js by _batch_id across the same "nfckey:cart-mutated" event, and for
 * each batch that disappears, POSTs {batch_id, approval_id} to
 * /apps/studio/api/embed/cart/removed so the studio app can void the approval
 * and revert the batch to an editable draft.
 */
(function () {
    'use strict';

    // Multi-app registry: window.NfckeyStudioApps = [{ key, origin, proxyPath }]
    // (injected sitewide from nfckey-cart-persistence.liquid). Each cart line
    // carries _studio_app_key; API calls that touch an app's own DB (sync
    // writes, removal cascade) are routed to THAT app's proxy path. Reads use
    // the default app — the persisted snapshot is one shared customer
    // metafield, identical through every app.
    // Fallback derives from THIS store's own registry (first entry), never a
    // hardcoded prod URL. Empty shape only if the metafield is missing entirely
    // (fail-safe on the current store, not a cross-store leak).
    var REGISTRY = (Array.isArray(window.NfckeyStudioApps) && window.NfckeyStudioApps.length)
        ? window.NfckeyStudioApps
        : [];
    // Shared resolver (#617): configured default key, then the literal `prod`
    // key, else empty. Never REGISTRY[0] — on the production store that is
    // `dev`, so a positional pick could route real customers to the dev host.
    var FALLBACK_APP = (function () {
        var empty = { key: '', origin: '', proxyPath: '' };
        if (typeof window.NfckeyResolveStudioApp === 'function') {
            return window.NfckeyResolveStudioApp(window.NfckeyStudioDefaultAppKey) || empty;
        }
        var keys = [window.NfckeyStudioDefaultAppKey, 'prod'];
        for (var k = 0; k < keys.length; k++) {
            if (!keys[k]) continue;
            for (var i = 0; i < REGISTRY.length; i++) {
                if (REGISTRY[i] && REGISTRY[i].key === keys[k]) return REGISTRY[i];
            }
        }
        return empty;
    })();
    var STUDIO_APPS = REGISTRY.length ? REGISTRY : [FALLBACK_APP];
    var DEFAULT_APP_KEY = window.NfckeyStudioDefaultAppKey || FALLBACK_APP.key || 'prod';

    function proxyPathForKey(appKey) {
        for (var i = 0; i < STUDIO_APPS.length; i++) {
            if (STUDIO_APPS[i].key === appKey && STUDIO_APPS[i].proxyPath) return STUDIO_APPS[i].proxyPath;
        }
        return FALLBACK_APP.proxyPath;
    }

    function appKeyForProperties(props) {
        if (!props) return DEFAULT_APP_KEY;
        if (props['_studio_app_key']) return props['_studio_app_key'];
        // Lines added via the app's own /cart/add redirect carry no explicit
        // key — infer it from the origin of the preview/image URLs, which
        // always point at the app that created the design.
        var inferUrl = props['_front_image_url'] || props['_preview_url'] || '';
        if (inferUrl) {
            for (var i = 0; i < STUDIO_APPS.length; i++) {
                if (STUDIO_APPS[i].origin && inferUrl.indexOf(STUDIO_APPS[i].origin) === 0) {
                    return STUDIO_APPS[i].key;
                }
            }
        }
        return DEFAULT_APP_KEY;
    }

    function originForKey(appKey) {
        for (var i = 0; i < STUDIO_APPS.length; i++) {
            if (STUDIO_APPS[i].key === appKey && STUDIO_APPS[i].origin) return STUDIO_APPS[i].origin;
        }
        return FALLBACK_APP.origin;
    }

    // Removal cascade (#191): diff /cart.js by _batch_id across the shared
    // "nfckey:cart-mutated" event (dispatched by designer-cart-validation.js's
    // existing fetch wrapper for /cart/change, /cart/update, /cart/add.js).
    // Runs independent of login/persistence — the endpoint itself is a no-op
    // for guests (no cascade, nothing to prune), so it is safe to always call.
    var lastKnownBatchLines = null; // batchId -> approvalId

    // Sequence guard (#194 / review §3.4): the initial seed fetch and a
    // mutation-triggered snapshot fetch can be in flight at the same time.
    // Without this, a late-arriving seed response (issued first, resolved
    // last) can overwrite a newer snapshot with stale/empty data, causing a
    // real removal to go undiffed. Only the response for the most recently
    // issued request is ever applied.
    var snapshotSeq = 0;
    var appliedSnapshotSeq = -1;

    function snapshotBatchLines(cart) {
        var out = {};
        (cart.items || []).forEach(function (item) {
            var props = item.properties || {};
            var batchId = props['_batch_id'];
            if (batchId) {
                out[batchId] = {
                    approvalId: props['_approval_id'] || '',
                    appKey: appKeyForProperties(props)
                };
            }
        });
        return out;
    }

    function showProjectsNotice(projectId, appKey) {
        if (!window.NFCKEY_CUSTOMER) return;
        var existing = document.getElementById('nfckey-cart-removed-notice');
        if (existing) existing.remove();

        var notice = document.createElement('div');
        notice.id = 'nfckey-cart-removed-notice';
        notice.setAttribute('role', 'status');
        notice.style.cssText =
            'position:fixed;z-index:9999;left:16px;right:16px;bottom:16px;max-width:480px;margin:0 auto;' +
            'padding:14px 16px;background:#111;color:#fff;border-radius:8px;font:14px/1.4 system-ui,sans-serif;' +
            'box-shadow:0 8px 24px rgba(0,0,0,.25);display:flex;gap:12px;align-items:flex-start;';

        var text = document.createElement('div');
        text.style.flex = '1';
        text.appendChild(document.createTextNode(
            'Item removed from cart. Your design was saved in Projects and is editable again. '
        ));
        var studioAppUrl = originForKey(appKey || DEFAULT_APP_KEY);
        var link = document.createElement('a');
        link.href = projectId
            ? studioAppUrl + '/projects/' + encodeURIComponent(projectId)
            : studioAppUrl + '/projects';
        link.textContent = 'Open Projects';
        link.style.cssText = 'color:#9ecbff;text-decoration:underline;';
        text.appendChild(link);
        notice.appendChild(text);

        var close = document.createElement('button');
        close.type = 'button';
        close.setAttribute('aria-label', 'Dismiss');
        close.textContent = '×';
        close.style.cssText =
            'border:0;background:transparent;color:#fff;font-size:20px;line-height:1;cursor:pointer;padding:0 4px;';
        close.addEventListener('click', function () { notice.remove(); });
        notice.appendChild(close);

        document.body.appendChild(notice);
        setTimeout(function () {
            if (notice.parentNode) notice.remove();
        }, 12000);
    }

    function notifyBatchRemoved(batchId, info) {
        // Routed to the app that minted the batch (its DB holds the approval).
        fetch(proxyPathForKey(info.appKey) + '/api/embed/cart/removed', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ batch_id: batchId, approval_id: info.approvalId || '' })
        })
            .then(function (res) { return res.json().catch(function () { return null; }); })
            .then(function (body) {
                if (!body || !window.NFCKEY_CUSTOMER) return;
                if (body.status === 'ok' || body.status === 'noop') {
                    showProjectsNotice(body.project_id || '', info.appKey);
                }
            })
            .catch(function (err) {
                console.error('[Cart Persistence] removal notify failed:', err);
            });
    }

    function diffAndNotifyRemovals(cart) {
        var current = snapshotBatchLines(cart);
        if (lastKnownBatchLines) {
            Object.keys(lastKnownBatchLines).forEach(function (batchId) {
                if (!(batchId in current)) {
                    notifyBatchRemoved(batchId, lastKnownBatchLines[batchId]);
                }
            });
        }
        lastKnownBatchLines = current;
    }

    function checkForRemovals() {
        var seq = ++snapshotSeq;
        fetch('/cart.js')
            .then(function (res) { return res.json(); })
            .then(function (cart) {
                if (seq <= appliedSnapshotSeq) return; // superseded by a later request
                appliedSnapshotSeq = seq;
                diffAndNotifyRemovals(cart);
            })
            .catch(function (err) {
                console.error('[Cart Persistence] removal check failed:', err);
            });
    }

    // Seed the initial snapshot on load so the first mutation has a "before"
    // state to diff against, then react to every subsequent mutation.
    (function seedInitialSnapshot() {
        var seq = ++snapshotSeq;
        fetch('/cart.js')
            .then(function (res) { return res.json(); })
            .then(function (cart) {
                if (seq <= appliedSnapshotSeq) return; // a mutation snapshot already landed
                appliedSnapshotSeq = seq;
                lastKnownBatchLines = snapshotBatchLines(cart);
            })
            .catch(function () {});
    })();
    document.addEventListener('nfckey:cart-mutated', checkForRemovals);

    if (!window.NFCKEY_CUSTOMER) return;

    // Reads (poll/GET) go through the default app — the snapshot is one shared
    // customer metafield, identical through every app's proxy. Writes (POST)
    // are grouped per app in syncNow(), because each app validates and owns
    // only the lines tagged with its key.
    var SYNC_READ_URL = proxyPathForKey(DEFAULT_APP_KEY) + '/api/embed/cart/sync';
    var DEBOUNCE_MS = 1800;
    var TTL_MS = 30 * 24 * 60 * 60 * 1000;

    var PROPERTY_KEYS = [
        '_batch_id', '_approval_id', '_design_version_hash', '_design_id', 'design_id',
        '_source', '_studio_app_key', '_front_image_url', '_back_image_url', 'Front Design', 'Back Design',
        'Design Name', 'Design ID', '_project_id', '_preview_url', 'preview_url',
        // Live-store code captured in #597. Dropped by the #440 rewrite; without
        // it a restored cart line loses its composited thumbnail and falls back
        // to round-tripping /api/batches/{id}/preview.
        '_keyfob_thumbnail_url',
        'Memory', 'UID Size', 'Proof Status',
        // #440/#554. This list is the one that actually strips: the server's
        // matching whitelist never sees a key the theme dropped before POSTing.
        // Without them a persisted fee line comes back with no pointer to the
        // design it pays for, which makes it invisible to the orphan sweep in
        // branding-fee-pairing.js — so it can never be removed and reappears on
        // every page load — and a restored DESIGN line loses `_branding_removed`,
        // so a cart restored on another device checks out paid-but-unflagged.
        '_branding_removed', '_branding_removal_for'
    ];

    var syncEnabled = false;

    // Tracks the newest live_cart updatedAt we know about, whether it came
    // from our own sync push, the SSR-embedded snapshot, or a poll response.
    // Reconcile (#198) only ever acts on a strictly newer timestamp than
    // this — which is what keeps a poll from re-applying our own just-sent
    // change (the response echoes the same updatedAt we already recorded)
    // and what stops a stale/in-flight poll from deleting a local line we
    // added but haven't synced yet (the stale response's updatedAt is older
    // than or equal to what we already know).
    var lastKnownRemoteUpdatedAt = null;

    function noteRemoteUpdatedAt(updatedAt) {
        if (!updatedAt) return;
        if (!lastKnownRemoteUpdatedAt || updatedAt > lastKnownRemoteUpdatedAt) {
            lastKnownRemoteUpdatedAt = updatedAt;
        }
    }

    // #198: the set of remote line keys from the last snapshot we actually
    // observed (SSR on load, or a poll response). Deletion is driven ONLY by
    // this: a local line is removed only if it was present here last time and
    // is now absent from a strictly-newer snapshot — a line we watched leave.
    // A freshly-added line that hasn't synced yet was never observed remotely,
    // so it can never be mistaken for a remote deletion and wiped. `null` means
    // "we have never observed a remote snapshot", which disables deletion until
    // we have a real baseline to diff against.
    var lastObservedRemoteKeys = null;
    // Prevents an interval poll and a visibilitychange poll that land together
    // from both running restore (/cart/add.js increments quantity on a dup).
    var reconcileInFlight = false;

    function keysFromLines(lines) {
        var keys = {};
        (Array.isArray(lines) ? lines : []).forEach(function (line) {
            if (line && line.variantId && line.quantity) keys[dedupeKeyFor(line)] = true;
        });
        return keys;
    }

    function pickProperties(properties) {
        var out = {};
        if (!properties) return out;
        PROPERTY_KEYS.forEach(function (key) {
            if (typeof properties[key] === 'string') {
                out[key] = properties[key];
            }
        });
        return out;
    }

    function fetchCart() {
        return fetch('/cart.js').then(function (res) { return res.json(); });
    }

    function toSyncLine(item) {
        var properties = pickProperties(item.properties);
        // Persist the resolved app key (explicit or inferred) so the owning
        // app accepts the line and other devices restore it already tagged.
        properties['_studio_app_key'] = appKeyForProperties(item.properties);
        return {
            variantId: item.variant_id,
            quantity: item.quantity,
            properties: properties
        };
    }

    function dedupeKeyFor(line) {
        var props = line.properties || {};
        var batchId = props['_batch_id'];
        if (batchId) return 'batch:' + batchId;
        // #554. Every branding fee is the SAME variant, so keying by variant
        // collapses two designs' fees into one line — the restored cart would
        // charge one fee for two clean designs. The fee's identity is the
        // design it points at, not the product.
        var feeFor = props['_branding_removal_for'];
        if (feeFor) return 'fee:' + feeFor;
        return 'variant:' + line.variantId;
    }

    function syncNow() {
        if (!syncEnabled) return;
        fetchCart()
            .then(function (cart) {
                var lines = (cart.items || []).map(toSyncLine);
                // One POST per registered app, each with only that app's lines.
                // The server merges: it keeps every line it does not own and
                // replaces only its own, so an empty group clears that app's
                // persisted lines without touching a sibling's (#238).
                //
                // Empty groups used to be skipped, because an app still running
                // the pre-merge code would have overwritten the WHOLE snapshot
                // with an empty list. Both registered apps now run the
                // merge-aware route (prod since v0.13.0), so the guard is gone —
                // removing an app's last line now clears it instead of leaving
                // a stale entry that reappears on the next restore.
                return Promise.all(STUDIO_APPS.map(function (app) {
                    var appLines = lines.filter(function (line) {
                        return line.properties['_studio_app_key'] === app.key;
                    });
                    return fetch(app.proxyPath + '/api/embed/cart/sync', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ lines: appLines })
                    }).catch(function () { return null; });
                }));
            })
            .then(function (responses) {
                return Promise.all((responses || []).map(function (res) {
                    return res && res.ok ? res.json().catch(function () { return null; }) : null;
                }));
            })
            .then(function (bodies) {
                (bodies || []).forEach(function (data) {
                    if (data && data.updatedAt) noteRemoteUpdatedAt(data.updatedAt);
                });
            })
            .catch(function (err) {
                console.error('[Cart Persistence] sync failed:', err);
            });
    }

    var debounceTimer = null;
    function scheduleSync() {
        if (!syncEnabled) return;
        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(syncNow, DEBOUNCE_MS);
    }

    document.addEventListener('nfckey:cart-mutated', scheduleSync);

    function isDesignerLine(properties) {
        return !!(properties && (properties['_design_id'] || properties['design_id']));
    }

    function brandingFeeVariantIds() {
        var el = document.getElementById('nfckey-branding-fee-variants');
        if (!el) return {};
        try {
            var ids = JSON.parse(el.textContent || '[]');
            var out = {};
            (Array.isArray(ids) ? ids : []).forEach(function (id) {
                var n = Number(id);
                if (Number.isFinite(n) && n > 0) out[n] = true;
            });
            return out;
        } catch (err) {
            return {};
        }
    }

    // Never restore an unbatched designer line (issue #157) — only proof lines
    // with a batch + approval, or plain non-designer products, are safe to re-add.
    // Also never restore an unpaired / propertyless branding fee (#554 follow-up):
    // that is what made the fee flash back every few seconds after the orphan
    // sweep removed it and the page reloaded into the same stale live_cart.
    function filterPersistedLines(lines) {
        var feeVariants = brandingFeeVariantIds();
        var liveBatchIds = {};
        (Array.isArray(lines) ? lines : []).forEach(function (line) {
            var batchId = line && line.properties && line.properties['_batch_id'];
            if (batchId) liveBatchIds[batchId] = true;
        });

        return (Array.isArray(lines) ? lines : []).filter(function (line) {
            if (!line || !line.variantId || !line.quantity) return false;
            var props = line.properties || {};
            var feeFor = props['_branding_removal_for'];
            if (feeFor) return !!liveBatchIds[feeFor];
            if (feeVariants[line.variantId]) return false;
            if (props['_batch_id']) return !!props['_approval_id'];
            return !isDesignerLine(props);
        });
    }

    function missingLines(persistedLines, localItems) {
        var localKeys = {};
        localItems.forEach(function (item) {
            localKeys[dedupeKeyFor(toSyncLine(item))] = true;
        });
        return persistedLines.filter(function (line) {
            return !localKeys[dedupeKeyFor(line)];
        });
    }

    function restoreLines(lines) {
        if (!lines.length) return Promise.resolve();
        return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                items: lines.map(function (line) {
                    return { id: line.variantId, quantity: line.quantity, properties: line.properties || {} };
                })
            })
        }).then(function (res) {
            // Restore happens without a page reload, so nothing else refreshes
            // the SSR cart section. Fire the theme's canonical cart:update
            // event (no `sections` in detail) so cart-items-component falls
            // back to sectionRenderer.renderSection and re-mounts cart blocks
            // — including proof-revalidation.liquid — against the new cart
            // (#188 / review §3.2, §3.5).
            if (res.ok) {
                document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { data: {} } }));
            }
            return res;
        }).catch(function (err) {
            console.error('[Cart Persistence] restore failed:', err);
        });
    }

    // #198: symmetric counterpart to restoreLines — removes local lines that
    // are no longer present in the synced snapshot (e.g. deleted on another
    // device). Uses /cart/change.js by line key, not variant id, so it only
    // ever touches the exact line that disappeared.
    function removeLocalLines(items) {
        if (!items.length) return Promise.resolve();
        return Promise.all(items.map(function (item) {
            return fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: item.key, quantity: 0 })
            });
        })).then(function (results) {
            if (results.some(function (res) { return res && res.ok; })) {
                document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { data: {} } }));
            }
            return results;
        }).catch(function (err) {
            console.error('[Cart Persistence] remove failed:', err);
        });
    }

    // #198: reconciles the local cart against a remote live_cart snapshot.
    //
    // ALWAYS additive-restores missing safe lines. Removal is deliberately
    // conservative: a local line is removed ONLY when `allowRemove` is set AND
    // the line was present in the previous remote snapshot we observed AND is
    // now absent from this strictly-newer one — i.e. a line we watched leave.
    //
    // This is the fix for the empty-cart regression: an earlier version deleted
    // any local line simply absent from remote, which wiped a freshly-added
    // line before it had synced (its variant isn't in the metafield yet) and,
    // on page load, emptied the entire cart against a stale/empty SSR snapshot.
    // `allowRemove` is false on the initial page-load reconcile, where we have
    // no trustworthy prior observation and a not-yet-synced add is common.
    function reconcileFromRemote(remote, allowRemove) {
        if (!remote || !remote.updatedAt) return Promise.resolve();
        if (lastKnownRemoteUpdatedAt && !(remote.updatedAt > lastKnownRemoteUpdatedAt)) {
            return Promise.resolve();
        }
        if (reconcileInFlight) return Promise.resolve();
        reconcileInFlight = true;

        var rawLines = Array.isArray(remote.lines) ? remote.lines : [];
        var addCandidates = filterPersistedLines(rawLines);
        var rawKeys = keysFromLines(rawLines);
        // Snapshot the previous observation BEFORE we overwrite it below —
        // deletion diffs against what we saw last time, not this new snapshot.
        var prevObserved = lastObservedRemoteKeys;

        return fetchCart().then(function (cart) {
            var localItems = cart.items || [];
            var toAdd = missingLines(addCandidates, localItems);

            var toRemove = [];
            if (allowRemove && prevObserved) {
                toRemove = localItems.filter(function (item) {
                    var key = dedupeKeyFor(toSyncLine(item));
                    return prevObserved[key] && !rawKeys[key];
                });
            }

            noteRemoteUpdatedAt(remote.updatedAt);
            lastObservedRemoteKeys = rawKeys;
            return Promise.all([restoreLines(toAdd), removeLocalLines(toRemove)]);
        }).then(function (r) {
            reconcileInFlight = false;
            return r;
        }).catch(function (err) {
            reconcileInFlight = false;
            console.error('[Cart Persistence] reconcile failed:', err);
        });
    }

    var POLL_MS = 20000;
    var pollTimer = null;

    function pollRemote() {
        if (!syncEnabled) return;
        fetch(SYNC_READ_URL)
            .then(function (res) { return res && res.ok ? res.json() : null; })
            .then(function (data) {
                // allowRemove: true — a live poll can propagate a genuine
                // remote deletion, but only for lines it previously observed
                // (see reconcileFromRemote), never a fresh un-synced add.
                if (data) reconcileFromRemote(data, true);
            })
            .catch(function (err) {
                console.error('[Cart Persistence] poll failed:', err);
            });
    }

    function startPolling() {
        if (pollTimer) return;
        pollTimer = setInterval(pollRemote, POLL_MS);
        // Catch up immediately when the tab regains focus instead of waiting
        // out the rest of the interval — covers switching back to a tab that
        // was open while another device changed the cart.
        document.addEventListener('visibilitychange', function () {
            if (document.visibilityState === 'visible') pollRemote();
        });
    }

    function enableSyncAndRun() {
        syncEnabled = true;
        if (typeof window.validateDesignerCart === 'function') {
            window.validateDesignerCart();
        }
        syncNow();
        startPolling();
    }

    // Issue #192: parse SSR cart from application/json textContent — never
    // assign metafield JSON into an executable <script> body.
    function readPersistedCart() {
        var el = document.getElementById('nfckey-persisted-cart');
        if (!el) return null;
        try {
            return JSON.parse(el.textContent || '');
        } catch (err) {
            console.error('[Cart Persistence] failed to parse persisted cart:', err);
            return null;
        }
    }

    function getValidPersistedSnapshot() {
        var persisted = readPersistedCart();
        if (!persisted || !persisted.updatedAt) return null;
        var age = Date.now() - new Date(persisted.updatedAt).getTime();
        if (!(age >= 0) || age > TTL_MS) return null; // stale: skip restore, best-effort clear via next sync
        return persisted;
    }

    function init() {
        var persisted = getValidPersistedSnapshot();

        if (!persisted) {
            enableSyncAndRun();
            return;
        }

        // #198: additive-only on load (allowRemove=false). A fresh add that
        // hasn't synced, or a line this device holds that predates our
        // knowledge of remote, must NEVER be deleted here — doing so emptied
        // carts against a stale/empty SSR snapshot. This reconcile still seeds
        // lastObservedRemoteKeys from the SSR snapshot, so the FIRST live poll
        // can already propagate a real remote deletion made while this tab was
        // open (it diffs against this baseline). The narrow gap that remains —
        // a line deleted on another device while THIS device was fully closed —
        // is left in place rather than risk wiping un-synced local adds.
        reconcileFromRemote(persisted, false)
            .then(enableSyncAndRun)
            .catch(function (err) {
                console.error('[Cart Persistence] init failed:', err);
                enableSyncAndRun();
            });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
